<template>
  <div class="w-full h-full flex flex-col gap-5">
    <div class="w-full">
      <h3 class="text-lg font-semibold mb-4 text-center">
        Summary Cargo Stolen in {{ chartTitle }}
      </h3>
      <div 
        class="w-full" 
        :style="{ height: '500px' }"
      >
        <bar-chart
          v-if="barChartData"
          :key="barChartData.labels[0]"
          class="w-full h-full"
          :data="barChartData"
          :options="barChartOptions"
          :plugins="barChartPlugins"
        />
        <div
          v-if="!barChartData"
          class="w-full h-32 flex items-center justify-center"
        >
          <span class="text-grey-001">No Data Found</span>
        </div>
      </div>
    </div>
    <div class="w-full px-5">
      <table class="w-full border-collapse border border-gray-300">
        <thead>
          <tr class="bg-gray-100">
            <th class="border border-gray-300 px-4 py-2 text-left" />
            <th
              v-for="label in chartLabels"
              :key="label"
              class="border border-gray-300 px-4 py-2 text-center"
            >
              {{ label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="border border-gray-300 px-4 py-2 font-semibold">
              <span class="inline-flex items-center gap-2">
                <span
                  class="w-3 h-3 rounded-full inline-block"
                  style="background-color: #7451D0;"
                />
                Total Visual Loss (Volume Mt)
              </span>
            </td>
            <td
              v-for="(value, index) in totalVisualLoss"
              :key="`loss-${index}`"
              class="border border-gray-300 px-4 py-2 text-center"
            >
              {{ value }}
            </td>
          </tr>
          <tr>
            <td class="border border-gray-300 px-4 py-2 font-semibold">
              <span class="inline-flex items-center gap-2">
                <span
                  class="w-3 h-3 rounded-full inline-block"
                  style="background-color: #FA1254;"
                />
                Total Accident
              </span>
            </td>
            <td
              v-for="(value, index) in totalAccident"
              :key="`accident-${index}`"
              class="border border-gray-300 px-4 py-2 text-center"
            >
              {{ value }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
    
<script>
import BarChart from "@/components/barChart";
import {tooltipConfig} from "@/utils/chartUtils";
import {handleValidateAndFormatNumber} from "@/utils/inputFormatter";
    
export default {
  props: {
    chartData: {
      type: Object,
      default: () => null
    },
    monthYear: {
      type: String,
      default: ''
    }
  },
  components: {
    BarChart
  },
  data() {
    return {
      isReady: false,
      barChartData: null
    }
  },
  computed: {
    chartTitle() {
      if (this.monthYear) {
        return this.monthYear.replace('-', ' ').toUpperCase()
      }
      return ''
    },
    processedData() {
      if (!this.chartData || typeof this.chartData !== 'object') {
        return {
          labels: [],
          totalVisualLoss: [],
          totalAccident: []
        }
      }

      // Extract week numbers and sort them
      const weekNumbers = Object.keys(this.chartData)
        .map(key => parseInt(key))
        .filter(num => !isNaN(num))
        .sort((a, b) => a - b)

      if (weekNumbers.length === 0) {
        return {
          labels: [],
          totalVisualLoss: [],
          totalAccident: []
        }
      }

      // Build labels and data arrays
      const labels = weekNumbers.map(week => `WEEK ${week}`)
      const totalVisualLoss = weekNumbers.map(week => {
        const weekData = this.chartData[week.toString()]
        return weekData?.total_visual_loss || 0
      })
      const totalAccident = weekNumbers.map(week => {
        const weekData = this.chartData[week.toString()]
        return weekData?.total_accident || 0
      })

      // Calculate totals
      const totalVisualLossSum = totalVisualLoss.reduce((sum, val) => sum + val, 0)
      const totalAccidentSum = totalAccident.reduce((sum, val) => sum + val, 0)

      // Add TOTAL to labels and data
      labels.push('TOTAL')
      totalVisualLoss.push(totalVisualLossSum)
      totalAccident.push(totalAccidentSum)

      return {
        labels,
        totalVisualLoss,
        totalAccident
      }
    },
    chartLabels() {
      return this.processedData.labels
    },
    totalVisualLoss() {
      return this.processedData.totalVisualLoss
    },
    totalAccident() {
      return this.processedData.totalAccident
    },
    maxAccident() {
      const values = this.processedData.totalAccident || []
      if (values.length === 0) return 5
      const max = Math.max(...values)
      // Round up to nearest 1, but ensure at least 5
      return Math.max(Math.ceil(max), 5)
    },
    visualLossStepSize() {
      const values = this.processedData.totalVisualLoss || []
      if (values.length === 0) return 100
      
      const sum = values.reduce((acc, val) => acc + val, 0)
      const avg = sum / values.length
      
      if (avg < 500) {
        return Math.max(50, Math.round(avg / 50) * 50) || 50
      } else {
        return Math.max(100, Math.round(avg / 100) * 100) || 100
      }
    },
    barChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        barPercentage: 0.8,
        categoryPercentage: 0.6,
        borderSkipped: false,
        borderRadius: {
          topLeft: 5,
          topRight: 5,
          bottomLeft: 5,
          bottomRight: 5
        },
        borderSkipped: 'middle',
        layout: {
          padding: {
            left: 25,
            top: 30,
            right: 25,
            bottom: 20
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            grid: {
              display: false
            },
            ticks: {
              color: 'black',
              font: {
                family: 'Poppins',
                size: 11
              },
              maxRotation: 0,
              minRotation: 0
            }
          },
          y: {
            type: 'linear',
            position: 'left',
            beginAtZero: true,
            ticks: {
              stepSize: this.visualLossStepSize,
              font: {
                family: 'Poppins',
                size: 11
              }
            },
            title: {
              display: true,
              text: 'Total Visual Loss (Volume Mt)',
              font: {
                family: 'Poppins',
                size: 11
              }
            },
            grid: {
              display: false
            }
          },
          y1: {
            type: 'linear',
            position: 'right',
            beginAtZero: true,
            max: this.maxAccident,
            ticks: {
              stepSize: 1,
              font: {
                family: 'Poppins',
                size: 11
              }
            },
            title: {
              display: true,
              text: 'Total Accident',
              font: {
                family: 'Poppins',
                size: 11
              }
            },
            grid: {
              display: false
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          chartAreaBorder: {
            borderColor: 'black',
            borderWidth: 2
          },
          axisIcons: true,
          datalabels: {
            display: true
          },
          tooltip: tooltipConfig({
            callbacks: {
              title(titleItem) {
                const item = titleItem[0]
                const dataIndex = item?.dataIndex
                return this.barChartData?.labels[dataIndex] || ''
              },
              label(tooltipItem) {
                const datasetLabel = tooltipItem?.dataset?.label || ''
                const value = handleValidateAndFormatNumber(tooltipItem?.raw)
                return `${datasetLabel}: ${value}`
              }
            }
          })
        }
      }
    },
    barChartPlugins() {
      return [
        this.chartAreaBorderPlugin(),
        this.axisIconPlugin(),
        this.barValuePlugin()
      ]
    }
  },
  mounted() {
    this.initializeChart()
  },
  methods: {
    initializeChart() {
      if (!this.processedData.labels.length) {
        this.barChartData = null
        return
      }
  
      this.barChartData = {
        labels: this.processedData.labels,
        datasets: [
          {
            label: 'Total Visual Loss (Volume Mt)',
            data: this.processedData.totalVisualLoss,
            backgroundColor: '#7451D0', // Purple
            borderColor: '#7451D0',
            borderWidth: 0,
            yAxisID: 'y',
            order: 2 // Draw behind the line chart
          },
          {
            type: 'line',
            label: 'Total Accident',
            data: this.processedData.totalAccident,
            borderColor: '#FA1254', // Red
            backgroundColor: '#FA1254',
            borderWidth: 2,
            borderDash: [5, 5],
            pointRadius: 5,
            pointStyle: 'rect',
            pointBackgroundColor: '#FA1254',
            pointBorderColor: '#FA1254',
            pointBorderWidth: 2,
            fill: false,
            yAxisID: 'y1',
            order: 1 // Draw in front of the bar chart
          }
        ]
      }
    },
    chartAreaBorderPlugin() {
      return {
        id: 'chartAreaBorder',
        beforeDraw(chart, args, options) {
          const {ctx, chartArea: {left, top, width, height}} = chart
          const extension = 15
          const padding = 1
    
          ctx.save();
          ctx.strokeStyle = options.borderColor || 'black'
          ctx.lineWidth = options.borderWidth || 2
    
          ctx.beginPath();
          ctx.moveTo(left, top - extension)
          ctx.lineTo(left, top + height + padding)
          ctx.stroke()
    
          ctx.beginPath()
          ctx.moveTo(left, top + height + padding)
          ctx.lineTo(left + width + extension, top + height + padding)
          ctx.stroke();
    
          ctx.restore()
        }
      }
    },
    axisIconPlugin() {
      return {
        id: 'axisIcons',
        beforeDraw(chart, args, options) {
          const { ctx, chartArea: { top, left, right, bottom }, scales } = chart;
  
          const topIcon = {
            text: '\u2BC5',
            x: left,
            y: top - 15,
            color: '#000',
            font: 'bold 20px FontAwesome'
          };
  
          const rightIcon = {
            text: '\u2BC8',
            x: right + 15,
            y: bottom + 2,
            color: '#000',
            font: 'bold 20px Poppins'
          }
  
          ctx.save()
          ctx.font = topIcon.font
          ctx.fillStyle = topIcon.color
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(topIcon.text, topIcon.x, topIcon.y)
          ctx.restore();
  
          ctx.save()
          ctx.font = rightIcon.font
          ctx.fillStyle = rightIcon.color
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(rightIcon.text, rightIcon.x, rightIcon.y)
          ctx.restore()
        }
      }
    },
    barValuePlugin() {
      return {
        id: 'barValue',
        afterDatasetsDraw(chart) {
          const { ctx, chartArea: { top, bottom }, scales } = chart;
          const datasets = chart.data.datasets;
            
          datasets.forEach((dataset, datasetIndex) => {
            const meta = chart.getDatasetMeta(datasetIndex);
              
            meta.data.forEach((element, index) => {
              const value = dataset.data[index];
                
              if (value !== null && value !== undefined && value !== 0) {
                const x = element.x;
                let y;
                  
                if (dataset.type === 'line') {
                  // For line chart, show value above the point
                  y = element.y - 15;
                } else {
                  // For bar chart, show value at the base of the bar
                  y = element.y + element.height - 10;
                }
                  
                ctx.save();
                ctx.fillStyle = '#000';
                ctx.font = '600 12px Poppins';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(value.toString(), x, y);
                ctx.restore();
              }
            });
          });
        }
      }
    }
  },
  watch: {
    chartData: {
      immediate: true,
      deep: true,
      handler() {
        this.initializeChart()
      }
    },
    processedData: {
      deep: true,
      handler() {
        this.initializeChart()
      }
    }
  }
}
</script>
    
    <style lang="scss" scoped>
    </style>